import React, { useEffect, useMemo, useState } from 'react';
import { Formik, Form as FormikForm } from 'formik';
import { App as AntdApp } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useDeleteHandler } from '../../hooks/useDeleteHandler';
import { EntityFormLayout } from '../../components/form/EntityFormLayout';
import { DynamicFormFields } from '../../components/form/DynamicFormFields';
import {
  documentFormFields,
  documentFormSchema,
  documentConfig,
  documentFormInitialValues,
} from './document.config';
import { Document, UpdateDocumentRequest } from './document.types';

const MAX_DOCUMENT_FILES = 10;

const DocumentForm: React.FC = () => {
  const { message } = AntdApp.useApp();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const mode: 'create' | 'edit' | 'view' = location.pathname.includes('/view/')
    ? 'view'
    : location.pathname.includes('/edit/')
      ? 'edit'
      : 'create';
  const [data, setData] = useState<Document | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchDocument = async () => {
    if (!id || mode === 'create') {
      setData(null);
      return;
    }
    try {
      setLoading(true);
      const res = await documentConfig.api.getOne(id);
      setData(res.data);
    } catch (error: any) {
      message.error(error?.message || 'Failed to fetch document');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, mode]);

  const existingFileNames = useMemo(() => {
    const fileNames = data?.fileNames;
    if (Array.isArray(fileNames) && fileNames.length > 0) return fileNames;
    if (data?.fileName) return [data.fileName];
    return [];
  }, [data]);

  const initialValues = useMemo(
    () => ({
      ...documentFormInitialValues(data),
      existingFiles: existingFileNames,
      removedExistingFiles: [],
    }),
    [data, existingFileNames]
  );

  const formFields = useMemo(
    () =>
      documentFormFields.map((field) =>
        field.name === 'files'
          ? {
              ...field,
              existingFiles: existingFileNames,
              maxFiles: MAX_DOCUMENT_FILES,
            }
          : field
      ),
    [existingFileNames]
  );

  const handleReset = () => {
    fetchDocument();
  };

  const handleSubmit = async (
    values: {
      name?: string;
      type?: string;
      files?: File[] | File | null;
      existingFiles?: string[];
      removedExistingFiles?: string[];
    },
    resetForm?: () => void
  ) => {
    try {
      setLoading(true);
      const filesValue = values.files;
      const selectedFiles = Array.isArray(filesValue) ? filesValue : filesValue ? [filesValue] : [];
      const existingFiles = Array.isArray(values.existingFiles)
        ? values.existingFiles.filter((fileName): fileName is string => !!fileName)
        : [];
      const removedExistingFiles = Array.isArray(values.removedExistingFiles)
        ? values.removedExistingFiles.filter((fileName): fileName is string => !!fileName)
        : [];
      if (selectedFiles.length + existingFiles.length > MAX_DOCUMENT_FILES) {
        message.error(`Maximum ${MAX_DOCUMENT_FILES} files are allowed`);
        return;
      }
      if (mode === 'create') {
        if (!selectedFiles.length) {
          message.error('Please select a valid file before saving');
          return;
        }
        const formData = new FormData();
        selectedFiles.forEach((file) => formData.append('files', file));
        if (values.name?.trim()) formData.append('name', values.name.trim());
        if (values.type?.trim()) formData.append('type', values.type);

        await documentConfig.api.create(formData);
        message.success(
          selectedFiles.length === 1
            ? 'Document uploaded successfully'
            : `${selectedFiles.length} documents uploaded successfully`
        );
        resetForm?.();
        return;
      } else if (id) {
        const updatePayload: UpdateDocumentRequest = {};
        const nextName = values.name?.trim() || '';
        const nextType = values.type || '';
        const currentName = data?.name || '';
        const currentType = data?.type || '';

        if (nextName !== currentName) updatePayload.name = nextName;
        if (nextType !== currentType) updatePayload.type = nextType;

        if (
          Object.keys(updatePayload).length === 0 &&
          selectedFiles.length === 0 &&
          removedExistingFiles.length === 0
        ) {
          message.info('No changes to update');
          return;
        }

        if (selectedFiles.length > 0 || removedExistingFiles.length > 0) {
          await documentConfig.api.updateWithFiles(id, {
            files: selectedFiles,
            ...(Object.prototype.hasOwnProperty.call(updatePayload, 'name') ? { name: updatePayload.name || '' } : {}),
            ...(Object.prototype.hasOwnProperty.call(updatePayload, 'type') ? { type: updatePayload.type || '' } : {}),
            existingFileNames: existingFiles,
            removeFileNames: removedExistingFiles,
          });
        } else {
          await documentConfig.api.update(id, updatePayload);
        }
        message.success('Document updated successfully');
      }
      navigate(documentConfig.redirectPath);
    } catch (error: any) {
      message.error(error?.message || `Failed to save ${documentConfig.entityName}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteHandler = useDeleteHandler();
  const handleDelete = async () => {
    if (!id) return;
    try {
      setLoading(true);
      await deleteHandler({
        id,
        entityName: documentConfig.entityName,
        deleteApi: documentConfig.api.delete,
        redirectPath: documentConfig.redirectPath,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <Formik
        key={`${id}-${mode}`}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={documentFormSchema}
        onSubmit={(values, helpers) => handleSubmit(values, helpers.resetForm)}
      >
        {({ dirty }) => (
          <FormikForm className="flex flex-col flex-grow h-full">
            <EntityFormLayout
              title={
                mode === 'create'
                  ? `Create ${documentConfig.entityName}`
                  : mode === 'edit'
                    ? `Edit ${documentConfig.entityName}`
                    : `View ${documentConfig.entityName}`
              }
              mode={mode}
              data={data}
              loading={loading}
              dirty={dirty}
              onGoBack={() => navigate(documentConfig.redirectPath)}
              onReset={handleReset}
              onDelete={handleDelete}
              onCreateNew={() => navigate(`${documentConfig.redirectPath}/create`)}
              onEdit={() => navigate(`${documentConfig.redirectPath}/edit/${id}`)}
              onView={() => navigate(`${documentConfig.redirectPath}/view/${id}`)}
            >
              <DynamicFormFields fields={formFields} mode={mode} />
            </EntityFormLayout>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
};

export default DocumentForm;
